import { inject, Injectable } from '@angular/core';
import { StateManagerService } from 'app/shared/state/state-manager.service';
import {
  BookingDetails,
  BookingDetailsSessionData,
  ContactInformation,
  IBookingDetailsState
} from 'app/views/booking-detail/models/booking-details.model';
import { map, Observable, switchMap, take } from 'rxjs';
import { BookingManagerResourceService } from './bookingManager.resource-service';
import { regularExpression } from './models/regular-expression';

@Injectable({
  providedIn: 'root'
})
export class BookingManagerDataService {
  private readonly _stateManager = inject(StateManagerService);
  private readonly bookingManagerService = inject(BookingManagerResourceService);

  public preparedDataViewModel(): Observable<IBookingDetailsState> {
    return this._stateManager.state$.pipe(
      take(1),
      map((state) => ({
        recordLocator: state.recordLocator!,
        stations: state.stations!,
        user: state.user!.userName,
        language: state.language!,
        email: state.user!.email
      })),
      switchMap((currentState: BookingDetailsSessionData) => {
        return this.retrieveBooking(currentState.recordLocator).pipe(
          map((booking) => ({
            state: currentState,
            booking: booking
          }))
        );
      })
    );
  }

  public retrieveBooking(recordLocator: string): Observable<BookingDetails> {
    return this.bookingManagerService.retrieveBooking(recordLocator).pipe(
      map((bd) => {
        return {
          ...bd,
          flightJourneys: [
            ...bd.flightJourneys.flatMap((journey) => {
              journey.segments.forEach((segment) => {
                segment.departureDate = new Date(segment.departureDate);
                segment.arrivalDate = new Date(segment.arrivalDate);
              });
              return journey;
            })
          ],
          paxContactInformation: this.transformContactInformation(bd.paxContactInformation)
        };
      })
    );
  }

  private transformContactInformation(contactInfo: ContactInformation | undefined): ContactInformation | undefined {
    if (!contactInfo) {
      return undefined;
    }

    return {
      ...contactInfo,
      phoneNumber: this.transformPhoneNumber(contactInfo.phoneNumber)
    };
  }

  private transformPhoneNumber(phoneNumber: string | undefined): string | undefined {
    if (!phoneNumber) {
      return undefined;
    }
    return regularExpression.test(phoneNumber) ? phoneNumber.replace(regularExpression, '') : phoneNumber;
  }
}
